// Small pieces the portfolio's panels share: the venue's fixed minimums, a ticking clock, and
// USDC figures as typed text. Every amount stays a decimal string or a BigInt.

import { useEffect, useState } from "react";
import { pt, type Minimums } from "../../sdk";
import { fmt, fromRaw, toRaw, USDC } from "../../lib/num";
import { clock, day } from "../../lib/time";

/** 10 USDC at 6 decimals: what the venue asks of a deposit and a withdrawal. */
const TEN_USDC = "10000000";

let held: Minimums | null = null;

/** The venue's fixed limits, read once from the SDK. */
export function minimums(): { deposit: bigint; withdrawal: bigint; activationFee: bigint } {
  if (!held) {
    try {
      held = pt.minimums();
    } catch {
      return { deposit: BigInt(TEN_USDC), withdrawal: BigInt(TEN_USDC), activationFee: 1_000_000n };
    }
  }
  return { deposit: BigInt(held.deposit), withdrawal: BigInt(held.withdrawal), activationFee: BigInt(held.activationFee) };
}

/** A raw USDC amount (6 decimals) as text an input takes: `12.5`. */
export const usdcText = (raw: bigint) => fromRaw(raw, USDC);

/** A raw USDC amount (6 decimals) for the screen, exact: `1,234.56`, `9.999`. */
export const usdcFig = (raw: bigint) => fmt(fromRaw(raw, USDC), { places: USDC, min: 2 });

/** A raw USDC amount floored to cents for a tight label: never more than is there. */
export const usdcCents = (raw: bigint) => fmt(fromRaw(raw - (raw % 10_000n), USDC), { places: 2 });

/** A raw USDC minimum in words: `10 USDC`. */
export const usdcMin = (raw: bigint) => `${fmt(fromRaw(raw, USDC), { places: USDC, min: 0 })} USDC`;

/** A decimal as Hyperliquid writes it, floored to USDC's 6 places. */
export function floorUsdc(dec: string | null | undefined): bigint {
  if (!dec) return 0n;
  const raw = toRaw(dec, USDC);
  return raw === null || raw < 0n ? 0n : raw;
}

/** Collateral (18 decimals) floored to USDC's 6. */
export const collateralToUsdc = (raw: bigint) => (raw > 0n ? raw / 10n ** 12n : 0n);

/** Typed text as raw USDC, or null when it is not an amount yet. */
export function typedUsdc(text: string): bigint | null {
  if (!text.trim()) return null;
  return toRaw(text, USDC);
}

/** The time now, ticking every `ms`. */
export function useNow(ms = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

/** `14:02:09` today, `14 Oct 2026` before. */
export function stamp(ms: number, now = Date.now()): string {
  return new Date(ms).toDateString() === new Date(now).toDateString() ? clock(ms) : day(ms);
}

export const sideWord = (isLong: boolean) => (isLong ? "Up" : "Down");
